import { rtkApi } from '@/shared/api/rtkApi';

export interface NewsItem {
  id: number;
  title: string;
  subtitle: string | null;
  body: string;
  coverImagePath: string | null;
  coverImageUrl: string | null;
  category: 'updates' | 'events' | 'important' | 'useful_materials';
  targetRoles: ('player' | 'owner' | 'admin')[];
  pinnedRoles: string[];
  isPinned: boolean;
  status: 'draft' | 'published';
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NewsListResponse {
  news: NewsItem[];
  total: number;
}

export interface NewsListArgs {
  category?: string;
  role?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export interface CreateNewsArgs {
  title: string;
  subtitle?: string | null;
  body: string;
  coverImagePath?: string | null;
  category: string;
  targetRoles: string[];
  pinnedRoles?: string[];
  status?: string;
  publishedAt?: string | null;
}

export interface UpdateNewsArgs extends Partial<CreateNewsArgs> {
  id: number;
}

export interface UploadNewsImageArgs {
  image: File;
  isCover?: boolean;
}

export interface UploadNewsImageResponse {
  path: string;
  url: string;
}

const newsApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getNewsList: build.query<NewsListResponse, NewsListArgs>({
      query: ({ category = '', role = '', status = '', limit = 20, offset = 0 }) => {
        const params = new URLSearchParams();
        if (category) params.append('category', category);
        if (role) params.append('role', role);
        if (status) params.append('status', status);
        params.append('limit', String(limit));
        params.append('offset', String(offset));
        return `/v1/superadmin/news?${params.toString()}`;
      },
      providesTags: (result) =>
        result
          ? [...result.news.map(({ id }) => ({ type: 'News' as const, id })), { type: 'News' as const, id: 'LIST' }]
          : [{ type: 'News' as const, id: 'LIST' }],
    }),
    getNewsDetail: build.query<NewsItem, number>({
      query: (id) => `/v1/superadmin/news/${id}`,
      providesTags: (result, error, id) => [{ type: 'News', id }],
    }),
    createNews: build.mutation<NewsItem, CreateNewsArgs>({
      query: (body) => ({
        url: '/v1/superadmin/news',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'News', id: 'LIST' }],
    }),
    updateNews: build.mutation<NewsItem, UpdateNewsArgs>({
      query: ({ id, ...body }) => ({
        url: `/v1/superadmin/news/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'News', id },
        { type: 'News', id: 'LIST' },
      ],
    }),
    deleteNews: build.mutation<{ status: string }, number>({
      query: (id) => ({
        url: `/v1/superadmin/news/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'News', id: 'LIST' }],
    }),
    uploadNewsImage: build.mutation<UploadNewsImageResponse, UploadNewsImageArgs>({
      query: ({ image, isCover = false }) => {
        const formData = new FormData();
        formData.append('image', image);
        const qs = isCover ? '?isCover=true' : '';
        return {
          url: `/v1/superadmin/news/upload-image${qs}`,
          method: 'POST',
          body: formData,
        };
      },
    }),
  }),
});

export const {
  useGetNewsListQuery,
  useGetNewsDetailQuery,
  useCreateNewsMutation,
  useUpdateNewsMutation,
  useDeleteNewsMutation,
  useUploadNewsImageMutation,
} = newsApi;
